import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { api } from "@/lib/api";
import { logError } from "@/lib/logger";
import { CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";

const MAX_ATTEMPTS = 40;
const IN_REVIEW_AFTER = 15;
const POLL_INTERVAL_MS = 2500;

function StatusView({ status, os }) {
  if (status === "checking") {
    return (
      <>
        <Clock className="mx-auto text-neutral-400 animate-pulse" size={48} />
        <h1 className="mt-6 font-heading font-black text-3xl">Confirmando pagamento…</h1>
        <p className="mt-3 text-neutral-600 text-sm">Estamos validando junto ao Mercado Pago. Aguarde alguns instantes.</p>
      </>
    );
  }
  if (status === "paid") {
    return (
      <>
        <CheckCircle2 className="mx-auto text-green-600" size={56} />
        <h1 className="mt-6 font-heading font-black text-3xl">Pagamento confirmado!</h1>
        <p className="mt-3 text-neutral-600">Seu reparo foi quitado. Pode passar para retirar sem preocupações.</p>
        {os && <Link to={`/rastrear/${os}`} className="mt-8 inline-block underline font-semibold" data-testid="success-back-tracking">Voltar ao acompanhamento</Link>}
      </>
    );
  }
  if (status === "in_review") {
    return (
      <>
        <AlertCircle className="mx-auto text-amber-500" size={56} />
        <h1 className="mt-6 font-heading font-black text-3xl">Pagamento em análise</h1>
        <p className="mt-3 text-neutral-600">O Mercado Pago está revisando esta transação. Assim que aprovada, você receberá a confirmação por e-mail e a OS ficará quitada automaticamente. Não é necessário pagar de novo.</p>
        {os && <Link to={`/rastrear/${os}`} className="mt-8 inline-block underline font-semibold" data-testid="review-back-tracking">Voltar ao acompanhamento</Link>}
      </>
    );
  }
  if (status === "rejected") {
    return (
      <>
        <XCircle className="mx-auto text-red-600" size={56} />
        <h1 className="mt-6 font-heading font-black text-3xl">Pagamento recusado</h1>
        <p className="mt-3 text-neutral-600">O Mercado Pago não aprovou este pagamento. Tente outro cartão ou pague via Pix.</p>
        {os && <Link to={`/checkout/${os}`} className="mt-8 inline-block underline font-semibold">Tentar novamente</Link>}
      </>
    );
  }
  // error | timeout
  return (
    <>
      <XCircle className="mx-auto text-red-600" size={56} />
      <h1 className="mt-6 font-heading font-black text-3xl">Não foi possível confirmar</h1>
      <p className="mt-3 text-neutral-600">Se o valor foi debitado, aguarde alguns minutos e recarregue a página — vamos consultar o Mercado Pago automaticamente e atualizar sua OS assim que o pagamento for aprovado.</p>
      {os && <Link to={`/rastrear/${os}`} className="mt-6 inline-block underline font-semibold" data-testid="timeout-back-tracking">Voltar ao acompanhamento</Link>}
    </>
  );
}

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("checking");
  const cancelledRef = useRef(false);

  const orderId = params.get("order");
  const os = params.get("os");
  const paymentId = params.get("payment_id") || params.get("collection_id");
  const mpStatus = params.get("status") || params.get("collection_status");

  useEffect(() => {
    cancelledRef.current = false;

    if (!orderId) { setStatus("error"); return; }
    if (mpStatus === "rejected") { setStatus("rejected"); return; }

    let attempts = 0;
    let timer = null;

    const poll = async () => {
      try {
        const qs = paymentId ? `?payment_id=${encodeURIComponent(paymentId)}` : "";
        const { data } = await api.get(`/payments/status/${orderId}${qs}`);
        if (cancelledRef.current) return;
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.mp_status === "rejected") { setStatus("rejected"); return; }
        if (data.mp_status === "in_process" && attempts > IN_REVIEW_AFTER) { setStatus("in_review"); return; }
        if (attempts > MAX_ATTEMPTS) { setStatus("timeout"); return; }
        attempts += 1;
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        logError("[payment-success] poll failed:", err);
        setStatus("error");
      }
    };
    poll();

    return () => {
      cancelledRef.current = true;
      if (timer) clearTimeout(timer);
    };
  }, [orderId, paymentId, mpStatus]);

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      <div className="max-w-lg mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
        <StatusView status={status} os={os} />
      </div>
    </div>
  );
}
